/**
 * Keeps the announcement bar's free-shipping progress in step with the cart.
 *
 * The bar is rendered by Liquid on load, so it is correct without JavaScript and correct on
 * a cold cache. This only handles the case Liquid cannot: the cart changing without a page
 * navigation - an add from a product card, a quantity change in the drawer, a line removal.
 *
 * It listens on Dawn's own `cartUpdate` pub/sub rather than polling, and falls back to a
 * single /cart.js read when an event arrives without cart data attached (older callers in
 * the theme publish only a source).
 */
if (!customElements.get('free-shipping-bar')) {
  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.threshold) || 0;
      this.messageEl = this.querySelector('.shipping-progress__text');
      this.unsubscribe = null;
      // Monotonic token so a slow /cart.js response can never clobber a newer
      // cartUpdate render that already landed (stale-request protection).
      this._req = 0;

      // PUB_SUB_EVENTS lives in global.js, which may load after this element upgrades.
      const attach = () => {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
          requestAnimationFrame(attach);
          return;
        }
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const total = event?.cartData?.total_price;
          if (typeof total === 'number') {
            // A live event is newer than any in-flight refetch.
            this._req += 1;
            this.render(total, event.cartData.item_count);
          } else {
            this.refetch();
          }
        });
        // Authoritative sync on connect: a plain refresh or cold load never fires a
        // cartUpdate event, so without this the bar could keep a stale server render.
        this.refetch();
      };
      attach();

      // Re-sync on every return to the page - normal reload AND bfcache restore -
      // guarded by the stale-request token so an old response cannot win.
      this._onPageShow = () => this.refetch();
      window.addEventListener('pageshow', this._onPageShow);
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this._onPageShow) window.removeEventListener('pageshow', this._onPageShow);
    }

    async refetch() {
      const req = ++this._req;
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        const body = await res.text();
        // Shopify serves /cart.js as `text/javascript`, NOT `application/json`. An earlier
        // version tested the content-type for "json" and bailed on every successful
        // response, so the bar only ever updated on a full page load. Detect the bot-check
        // interstitial by its markup instead, which is what that guard was actually for.
        if (/^\s*</.test(body)) return;
        const cart = JSON.parse(body);
        if (req !== this._req) return; // a newer event or fetch superseded this one
        this.render(cart.total_price, cart.item_count);
      } catch (e) {
        /* leave the server-rendered state alone rather than showing a wrong number */
      }
    }

    /** Formats minor units with the shop's own money format, falling back to a plain sum. */
    money(cents) {
      const fmt = window.Shopify?.currency?.active;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'he-IL', {
          style: 'currency',
          currency: fmt || 'ILS',
          maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
        }).format(cents / 100);
      } catch (e) {
        return `${(cents / 100).toFixed(0)}`;
      }
    }

    render(totalPrice, itemCount) {
      if (!this.messageEl) return;

      // Guard: if totalPrice is 0 but itemCount > 0, the cart data is stale/invalid.
      // Keep the server-rendered state instead of showing a wrong number.
      if (typeof totalPrice !== 'number' || isNaN(totalPrice)) return;
      if (totalPrice === 0 && itemCount > 0) return;

      const remaining = this.threshold - totalPrice;
      const qualified = remaining <= 0;
      const percent = this.threshold > 0 ? Math.min(100, (totalPrice / this.threshold) * 100) : 0;

      this.classList.toggle('shipping-progress--qualified', qualified);

      if (!itemCount) {
        this.messageEl.textContent = this.dataset.emptyText;
      } else if (qualified) {
        this.messageEl.textContent = this.dataset.qualifiedText;
      } else {
        this.messageEl.textContent = this.dataset.progressText.replace('__AMOUNT__', this.money(remaining));
      }

      // Mark that we've successfully rendered valid cart data
      this._hasRendered = true;

      let track = this.querySelector('.shipping-progress__track');
      if (!itemCount) {
        if (track) track.remove();
        return;
      }
      if (!track) {
        track = document.createElement('div');
        track.className = 'shipping-progress__track';
        track.setAttribute('role', 'progressbar');
        track.setAttribute('aria-valuemin', '0');
        track.setAttribute('aria-valuemax', '100');
        track.innerHTML = '<span class="shipping-progress__fill"></span>';
        this.appendChild(track);
      }
      track.setAttribute('aria-valuenow', String(Math.round(percent)));
      track.querySelector('.shipping-progress__fill').style.setProperty('--shipping-progress', `${percent}%`);
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
}
